import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import { billingAPI, PaymentMethod } from '../../services/api';

interface PaymentMethodListProps {
  onAddNew: () => void;
}

export const PaymentMethodList: React.FC<PaymentMethodListProps> = ({
  onAddNew,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { confirm } = useConfirm();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  const loadPaymentMethods = async () => {
    try {
      const response = await billingAPI.getPaymentMethods();
      setPaymentMethods(response.data.payment_methods);
    } catch (err) {
      setError(t('billing.errors.loadPaymentMethods'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (id: number) => {
    setActionLoading(id);
    try {
      await billingAPI.setDefaultPaymentMethod(id);
      await loadPaymentMethods();
    } catch (err) {
      setError(t('billing.errors.setDefault'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (id: number) => {
    const confirmed = await confirm({
      title: t('billing.removePaymentMethodTitle'),
      message: t('billing.confirmRemovePaymentMethod'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    setActionLoading(id);
    try {
      await billingAPI.removePaymentMethod(id);
      await loadPaymentMethods();
    } catch (err: any) {
      setError(err.response?.data?.error || t('billing.errors.removePaymentMethod'));
    } finally {
      setActionLoading(null);
    }
  };

  const getCardIcon = (brand?: string): string => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return '💳 Visa';
      case 'mastercard':
        return '💳 Mastercard';
      case 'amex':
        return '💳 Amex';
      default:
        return '💳';
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className={`rounded-xl border p-4 animate-pulse ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <div className={`h-5 rounded w-1/3 mb-2 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
            <div className={`h-4 rounded w-1/4 ${isDark ? 'bg-slate-700' : 'bg-gray-200'}`}></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className={`px-4 py-3 rounded-xl text-sm ${isDark ? 'bg-red-500/10 border border-red-500/30 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {error}
          <button
            onClick={() => setError(null)}
            className={`ml-2 ${isDark ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-700'}`}
          >
            ×
          </button>
        </div>
      )}

      {paymentMethods.length === 0 ? (
        <div className={`text-center py-8 rounded-xl ${isDark ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-gray-50'}`}>
          <p className={`mb-4 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{t('billing.noPaymentMethods')}</p>
          <button
            onClick={onAddNew}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all"
          >
            {t('billing.addPaymentMethod')}
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {paymentMethods.map((pm) => (
              <div
                key={pm.id}
                className={`rounded-xl border p-4 flex items-center justify-between transition-all ${
                  pm.expired
                    ? isDark ? 'border-red-500/30 bg-red-500/10' : 'border-red-300 bg-red-50'
                    : pm.expiring_soon
                      ? isDark ? 'border-amber-500/30 bg-amber-500/10' : 'border-yellow-300 bg-yellow-50'
                      : isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center space-x-4">
                  <span className="text-2xl">{getCardIcon(pm.card_brand)}</span>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {pm.display_name}
                      </span>
                      {pm.is_default && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-blue-100 text-blue-800'}`}>
                          {t('billing.default')}
                        </span>
                      )}
                      {pm.expired && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-800'}`}>
                          {t('billing.expired')}
                        </span>
                      )}
                      {pm.expiring_soon && !pm.expired && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-yellow-100 text-yellow-800'}`}>
                          {t('billing.expiringSoon')}
                        </span>
                      )}
                    </div>
                    {pm.expiry_display && (
                      <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {t('billing.expires')}: {pm.expiry_display}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {!pm.is_default && (
                    <button
                      onClick={() => handleSetDefault(pm.id)}
                      disabled={actionLoading === pm.id}
                      className="text-sm text-cyan-500 hover:text-cyan-400 disabled:opacity-50 transition-colors"
                    >
                      {t('billing.makeDefault')}
                    </button>
                  )}
                  <button
                    onClick={() => handleRemove(pm.id)}
                    disabled={actionLoading === pm.id}
                    className={`text-sm disabled:opacity-50 transition-colors ${isDark ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'}`}
                  >
                    {t('billing.remove')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={onAddNew}
            className={`w-full py-2 border-2 border-dashed rounded-xl transition-colors ${isDark ? 'border-slate-600 text-slate-400 hover:border-cyan-500 hover:text-cyan-500' : 'border-gray-300 text-gray-500 hover:border-blue-500 hover:text-blue-500'}`}
          >
            + {t('billing.addPaymentMethod')}
          </button>
        </>
      )}
    </div>
  );
};

export default PaymentMethodList;
